import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useToast } from "vue-toastification";
import { event } from "vue-gtag";
import i18n from "@/i18n";
import { Organisation } from "@/types/company";
import { Scopes } from "@/types/user";

export const useAppStore = defineStore("app", () => {
  const toast = useToast();

  const route = useRoute();
  const router = useRouter();

  const currentRoute = ref(null);

  const currentOrganisationId = computed(() => {
    return route.params.companyId as string
  });
  const currentCampaignId = computed(() => {
    if (onboardingIsLive.value) return "8d9740ed-56ac-4f1f-856f-df9d81f16c52";
    return route.params.campaignId as string
  });
  const currentSpaceId = computed(() => {
    return route.params.spaceId as string
  });

  // Onboarding
  const isOnboarded = ref(false);
  const onboardingIsLive = ref(false);
  const onboardingPage = ref('');
  const chapterState = ref(null);
  const skipStep = ref(false);
  const forceExit = ref(false);

  // Sidebar
  const sidebarIsMounted = ref(false);
  const sidebarIsOpen = ref(false);
  const sidebarFooterIsOpen = ref(false);

  // Campaign
  const currentCampaignTab = ref('Overview');
  const activatedTableHasNews = ref({});
  const ponderationIsVisible = ref(false);
  const showCampaignLinkInfo = ref(false);
  const showCampaignParameters = ref(false);
  const campaignParametersTab = ref(0);
  
  // Talent panel
  const talentResultPanelIsOpen = ref(false);
  const openedCandidateResults = ref([]);
  const talentPanelDisplayMode = ref('LIST'); // can be LIST or GRAPH cf. enum DisplayMode
  const talentPanelPonderationIsOpen = ref(false);

  // Payment
  const creditsNeeded = ref(1);

  // News
  const viewedNewsIds = ref([]);
  const skipNewsForSession = ref(false);

  // Load viewed news IDs from localStorage
  if (localStorage.getItem('viewedNews')) {
    try {
      viewedNewsIds.value = JSON.parse(localStorage.getItem('viewedNews'));
    } catch (e) {
      viewedNewsIds.value = [];
    }
  }

  const currentNews = computed(() => {
    const { tm } = i18n.global;

    const newsRaw = tm("news", { returnObjects: true });
    const news = Object.entries(newsRaw).map(([id, item]) => ({ id, ...item }));

    if (!news || news.length === 0 || skipNewsForSession.value) return null;

    // Find the first news that hasn't been viewed and is within date range
    const now = new Date();
    for (let i = news.length - 1; i >= 0; i--) {
      const newsItem = news[i];

      // Check if news has date limits
      if (newsItem.validUntil && new Date(newsItem.validUntil) < now) {
        continue; // Skip this news if it's expired
      }

      if (newsItem.validFrom && new Date(newsItem.validFrom) > now) {
        continue; // Skip this news if it's not yet valid
      }

      // Return this news if it hasn't been viewed
      if (!viewedNewsIds.value.includes(newsItem.id)) {
        return newsItem;
      }
    }

    return null; // No valid news to show
  });

  if (localStorage.getItem("isOnboarded")) {
    isOnboarded.value = JSON.parse(localStorage.getItem("isOnboarded"));
  }

  watch(
    isOnboarded,
    (value) => {
      localStorage.setItem("isOnboarded", value);
    },
    { deep: true }
  );

  function openTalentPanel() {
    talentResultPanelIsOpen.value = false;
  }

  // News funcs
  function dismissNews(dontShowAgain) {
    if (currentNews.value) {
      if (dontShowAgain) {
        // Add current news ID to viewed list
        viewedNewsIds.value.push(currentNews.value.id);

        // Save to localStorage
        localStorage.setItem('viewedNews', JSON.stringify(viewedNewsIds.value));
      }

      // No matter what, prevent showing more news until next refresh
      skipNewsForSession.value = true;
    }
  }

  // Sidebar funcs
  function toggleSidebar(value) {
    sidebarIsOpen.value = value;

    toggleSidebarFooter(false);
  }

  function toggleSidebarFooter(value) {
    sidebarFooterIsOpen.value = value;
  }

  // Campaign funcs
  async function changeTab(tab) {
    await router.replace({
      query: { ...route.query, tab: tab },
    });
  }

  // Campaign funcs for onboarding
  function goAddCandidatesOnBoard() {
    if (onboardingPage.value != "addCandidates") {
      this.$router.push({
        name: "addCandidates",
        params: {
          campaignId: '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
        }
      });
      onboardingPage.value = "addCandidates";
    }
  }

  // Go back to campaign funcs for onboarding
  function goBackCampaignOnBoard() {
    if (onboardingPage.value != "campaign") {
      this.$router.push({ name: "campaign-demonstration" });
      onboardingPage.value = "campaign";
    }
  }

  // Onboarding funcs
  function restartOnboarding() {
    isOnboarded.value = false;
    onboardingIsLive.value = false;
    this.$router.push({ name: "root" });
    onboardingPage.value = "home";
  }

  function exitOnboarding() {
    forceExit.value = true;
    this.$introJs.exit(true);
  }

  function startChapter(chapterData) {
    const { t } = i18n.global;

    if (isOnboarded.value) return;

    chapterState.value = chapterData;

    const { steps, afterComplete, nextRoute } = chapterState.value;

    onboardingIsLive.value = true;
    chapterState.value.isComplete = false;

    this.$introJs
      .onchange((targetElement) => {
        const currStep = this.$introJs.currentStep();
        
        if (currStep === 0) {
          if (steps[currStep].before) {
            steps[currStep].before();
            setTimeout(() => {
              this.$introJs.refresh({ refreshSteps: true });
              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }, steps[currStep].waitChange);
          }
        }
      })
      .onbeforechange(async (targetElement) => {
        const currStep = this.$introJs.currentStep() + 1;

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }

        if (currStep > 1 && currStep <= steps.length && !skipStep.value) {
          if (steps[currStep - 1].before) {
            steps[currStep - 1].before();

            setTimeout(() => {
              skipStep.value = true;
              this.$introJs.refresh({ refreshSteps: true });
              this.$introJs.goToStep(currStep);
            }, steps[currStep - 1].waitChange);

            return false;
          }
        } else {
          skipStep.value = false;
        }
      })
      .onbeforeexit(() => {
        if (!chapterState.value.isComplete && !forceExit.value) {
          return window.confirm(
            t('app.onboarding.confirmQuit')
          );
        }
      })
      .onexit(() => {
        if (!chapterState.value.isComplete) {
          toast.warning(
            t('app.onboarding.toasts.redo')
          );
          isOnboarded.value = true;
          onboardingIsLive.value = false;
          this.$router.push({ name: "root" });
        }

        forceExit.value = false;
      })
      .oncomplete((step, mode) => {
        if (mode !== "skip") {
          chapterState.value.isComplete = true;
          if (afterComplete) afterComplete();

          if (nextRoute) this.$router.push(nextRoute);
        }
      })
      .setOptions(chapterState.value)
      .start();
  }

  async function exportPdf(pdfType, participantId) {
    try {
      const { data } = await useAxios.get(`/api/v1/participations/${participantId}/pdf/${pdfType}`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async function exportCsv(campaignId) {
    try {
      const { data } = await useAxios.get(`/api/v1/campaigns/${campaignId}/csv`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  return {
    currentCampaignId,
    currentOrganisationId,
    currentSpaceId,
    activatedTableHasNews,
    openedCandidateResults,
    talentResultPanelIsOpen,
    talentPanelDisplayMode,
    talentPanelPonderationIsOpen,
    chapterState,
    creditsNeeded,
    currentCampaignTab,
    currentRoute,
    isOnboarded,
    onboardingIsLive,
    ponderationIsVisible,
    showCampaignLinkInfo,
    showCampaignParameters,
    campaignParametersTab,
    sidebarFooterIsOpen,
    sidebarIsMounted,
    sidebarIsOpen,
    openTalentPanel,
    exitOnboarding,
    toggleSidebar,
    toggleSidebarFooter,
    changeTab,
    goAddCandidatesOnBoard,
    goBackCampaignOnBoard,
    restartOnboarding,
    startChapter,
    currentNews,
    dismissNews,
    exportPdf,
    exportCsv,
  };
});
